import { Box, Paper, useTheme } from '@mui/material';
import { Typography } from '@mui/material';
import type { GridPaginationModel } from '@mui/x-data-grid-pro';
import { useEffect, useRef } from 'react';
import { useIntl } from 'react-intl';
import { useLocation } from 'react-router-dom';

import {
  useAppDispatch,
  useAppSelector,
  DATAGRID_PAGE_SIZE_OPTIONS,
} from '@verantos-dev/core-annotation-tool';
import { DataGrid, DataGridLayout, Dropdown } from '@verantos-dev/shared-components';
import { DataGridToolbar } from '@verantos-dev/ui-annotation-tool';

import { useAnnotatorQueueContent } from './useAnnotatorQueueContent';
import { useAnnotationTaskDataGridFetch } from '../hook/useDatagridAnnotatorTaskFetch';
import { setProjectsListPaginationModel } from '../state/annotatorQueueSlice';
import {
  selectBatchId,
  selectProjectId,
  selectProjectsListPage,
  selectProjectsListPageSize,
} from '../state/selector';

export const AnnotatorQueueContent = () => {
  const {
    annotationProjectOptions,
    isAnnotationProjectsLoading,
    batchOptions,
    isBatchesLoading,
    handleProjectChange,
    handleBatchChange,
    columns,
  } = useAnnotatorQueueContent();
  const { formatMessage } = useIntl();
  const theme = useTheme();
  const projectId = useAppSelector(selectProjectId);
  const batchId = useAppSelector(selectBatchId);
  const dispatch = useAppDispatch();
  const projectsListPage = useAppSelector(selectProjectsListPage);
  const projectsListPageSize = useAppSelector(selectProjectsListPageSize);

  const handlePaginationModelChange = (model: GridPaginationModel) => {
    dispatch(setProjectsListPaginationModel({ page: model.page, pageSize: model.pageSize }));
  };

  const prevProjectRef = useRef(projectId);
  const prevBatchRef = useRef(batchId);

  useEffect(() => {
    if (prevProjectRef.current !== projectId || prevBatchRef.current !== batchId) {
      dispatch(setProjectsListPaginationModel({ page: 0, pageSize: projectsListPageSize }));
    }

    prevProjectRef.current = projectId;
    prevBatchRef.current = batchId;
  }, [projectId, batchId]);

  const location = useLocation();

  const { refetch } = useAnnotationTaskDataGridFetch() || {};

  useEffect(() => {
    if (projectId && batchId) {
      refetch();
    }
  }, [location.key, projectId, batchId]);

  const isReqSelectionDone = projectId && batchId;

  const emptyStateMessageIds =
    annotationProjectOptions.length === 0
      ? {
          title: 'workspaceCommon.content.table.noProjectAssigned.title',
          description: 'workspaceCommon.content.table.noProjectAssigned.description',
        }
      : !projectId
      ? {
          title: 'workspaceCommon.content.table.noProjectSelected.title',
          description: 'workspaceCommon.content.table.noProjectSelected.description',
        }
      : !isBatchesLoading && batchOptions.length === 0
      ? {
          title: 'workspaceCommon.content.table.noBatchesAvailable.title',
          description: 'workspaceCommon.content.table.noBatchesAvailable.description',
        }
      : {
          title: 'workspaceCommon.content.table.noBatchSelected.title',
          description: 'workspaceCommon.content.table.noProjectSelected.description',
        };

  return (
    <Box>
      <Box sx={{ display: 'flex', gap: theme.spacing(4), pb: theme.spacing(4) }}>
        <Dropdown
          id="annotator-queue-content-project-dropdown"
          options={annotationProjectOptions}
          onChange={handleProjectChange}
          label={formatMessage({ id: 'workspaceCommon.content.dropdown.project.label' })}
          placeholder={formatMessage({
            id: 'workspaceCommon.content.dropdown.project.placeholder',
          })}
          disabled={isAnnotationProjectsLoading || annotationProjectOptions.length === 0}
          width="medium"
          value={projectId ?? ''}
        />
        <Dropdown
          id="annotator-queue-content-batch-dropdown"
          options={batchOptions}
          onChange={handleBatchChange}
          label={formatMessage({ id: 'workspaceCommon.content.dropdown.batch.label' })}
          placeholder={formatMessage({ id: 'workspaceCommon.content.dropdown.batch.placeholder' })}
          disabled={isBatchesLoading || batchOptions.length === 0}
          width="medium"
          value={batchId ?? ''}
        />
      </Box>
      {!isReqSelectionDone ? (
        <Box>
          <Paper
            sx={{
              height: '348px',
              display: 'flex',
              flexDirection: 'column',
              justifyContent: 'center',
              alignItems: 'center',
              gap: theme.spacing(2),
            }}
          >
            <Typography variant="h5">
              {formatMessage({ id: emptyStateMessageIds.title })}
            </Typography>
            <Typography variant="body1" sx={{ color: theme.palette.text.secondary }}>
              {formatMessage({ id: emptyStateMessageIds.description })}
            </Typography>
          </Paper>
        </Box>
      ) : (
        <DataGridLayout id="annotator-queue-content-data-grid">
          <DataGrid
            id="annotator-queue-content-data-grid"
            mode="client"
            pagination
            loadOnFetch
            pageSizeOptions={DATAGRID_PAGE_SIZE_OPTIONS}
            paginationModel={{ page: projectsListPage, pageSize: projectsListPageSize }}
            onPaginationModelChange={handlePaginationModelChange}
            disableSearchParamSync
            preventUrlReadWhenSyncDisabled
            getRowId={(row) => row.id}
            columns={columns}
            useFetchDataGridData={useAnnotationTaskDataGridFetch}
            slots={{ toolbar: DataGridToolbar }}
            sx={{
              maxHeight: '70vh',
              '& .MuiDataGrid-row': {
                borderBottom: `1px solid ${theme.palette.divider}`,
              },
              '& .MuiDataGrid-cell--pinnedLeft': {
                borderRight: `2px solid ${theme.palette.divider}`,
              },
              '& .MuiDataGrid-columnHeader--pinnedLeft': {
                borderRight: `2px solid ${theme.palette.divider}`,
              },
            }}
            initialState={{
              pinnedColumns: {
                left: ['encounterId'],
              },
              sorting: {
                sortModel: [
                  {
                    field: 'status',
                    sort: 'asc',
                  },
                ],
              },
            }}
          />
        </DataGridLayout>
      )}
    </Box>
  );
};
